import { createSlice } from '@reduxjs/toolkit';
import { notifyUserError, notifyUserSuccess } from 'utils/notifications';

import {
  deleteAddendum,
  deleteNoteInput,
  getAddendums,
  getClientNote,
  getClientNotes,
  getFavoriteZCodes,
  getSites,
  getUsersImageNotes,
  patchEditAddendum,
  postCreateAddendum,
  putEditAddendum,
  getZCodes
} from './clientProfileActions';

export type ZCode = {
  id: number;
  code: string;
  description: string;
  explanations: {
    id: number;
    explanation: string;
  }[];
  selectedExplanations: { id: number; explanation: string }[];
};

export type AiScribeStatus =
  | 'TRANSCRIBE_STARTED'
  | 'TRANSCRIBE_IN_PROGRESS'
  | 'TRANSCRIBE_COMPLETED'
  | 'SUMMARIZING'
  | 'COMPLETED'
  | 'FAILED';
export interface ClientProfileContentItem {
  uuid: string;
  chwId: number;
  managerId: number;
  billingProviderId: string;
  createdBy: number;
  createdAt: string;
  updatedBy: number;
  updatedAt: string;
  id: number;
  noteType: string;
  clientReached: string;
  hasAddendums: boolean;
  dateOfEncounter: string;
  startTimeOfEncounter: string;
  nextContact: string;
  timeSpentMin: number;
  timeSpent: number;
  typeOfSupport: string[];
  note: string;
  author: {
    id: number;
    name: string;
    profileImageKey: string;
    profileImageUrl?: string;
  };
  draft: boolean;
  siteId: number | null;
  siteName: string | null;
  selectedZCodes: ZCode[];
  aiScribe: {
    status: AiScribeStatus;
    transcript: string | null;
    errorMessage: string | null;
    audioUrl: string | null;
    fileUuid: string;
  } | null;
  documentationTime: number | null;
  whoDidYouReach: string[];
  whoWereYouTryingToReach: string;
  howDidYouTryThem: string;
  howDidYouReachThem: string;
  whatTypeOfPlaceWereYouIn: string;
  otherProviderName: string;
}

type Status = 'DRAFT' | 'FINAL';

type Author = {
  id: number;
  name: string;
  profileImageKey: string;
  profileImageUrl?: string;
};

export type Addendum = {
  id: number;
  noteId: number;
  content: string;
  status: Status;
  author: Author;
  createdBy: number;
  createdAt: string;
  updatedBy: number;
  updatedAt: string;
};

type Site = {
  id: number;
  name: string;
};

export interface ClientProfileSliceProps {
  clientProfileList: {
    notesList: ClientProfileContentItem[];
    page: number;
    size: number;
    totalNumber: number | null;
    loading: boolean;
  };
  clientNotes: {
    [key: string]: {
      loading: boolean;
      data: ClientProfileContentItem | null | undefined;
    };
  };
  sites: {
    data: Site[];
    loading: boolean;
  };
  addendums: {
    data: Addendum[];
    loading: boolean;
  };
  favoriteZCodes: {
    data: ZCode[];
    loading: boolean;
  };
  zCodes: {
    data: ZCode[];
    loading: boolean;
  };
}

const initialState: ClientProfileSliceProps = {
  clientProfileList: {
    notesList: [],
    page: 0,
    size: 5,
    totalNumber: null,
    loading: false
  },
  clientNotes: {},
  sites: {
    data: [],
    loading: false
  },
  addendums: {
    data: [],
    loading: false
  },
  zCodes: {
    data: [],
    loading: false
  },
  favoriteZCodes: {
    data: [],
    loading: false
  }
};

export const clientProfileSlice = createSlice({
  name: 'clientProfileSlice',
  initialState,
  reducers: {
    resetClientNotes: (state) => {
      state.clientProfileList = {
        notesList: [],
        page: 0,
        size: 5,
        totalNumber: null,
        loading: false
      };
    },
    deleteNoteInClientNotes: (state, action) => {
      const noteId = action.payload;
      state.clientProfileList.notesList = state.clientProfileList.notesList.filter(
        (note) => note.id !== Number(noteId)
      );
      state.clientProfileList.totalNumber =
        (typeof state.clientProfileList.totalNumber === 'number' &&
          state.clientProfileList.totalNumber - 1) ||
        state.clientProfileList.totalNumber;
    },
    updateNoteInClientNotes: (state, action) => {
      const noteIndex = state.clientProfileList.notesList.findIndex(
        (note) => note.id === Number(action.payload.id)
      );

      if (noteIndex !== -1) {
        state.clientProfileList.notesList[noteIndex] = {
          ...state.clientProfileList.notesList[noteIndex],
          ...action.payload,
          id: state.clientProfileList.notesList[noteIndex].id
        };
      }
    },
    removeNote: (state) => {
      state.clientNotes = {};
    },
    clearZCodes: (state) => {
      state.zCodes.data = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getClientNotes.pending, (state, { payload }: any) => {
        state.clientProfileList.loading = true;
      })
      .addCase(getClientNotes.fulfilled, (state, { payload }: any) => {
        const existingIds = new Set(state.clientProfileList.notesList.map((note) => note.id));
        const uniqueNotes = payload.data.content.filter((note: any) => !existingIds.has(note.id));

        state.clientProfileList = {
          ...payload.data,
          loading: false,
          notesList: payload.firstRender
            ? payload.data.content
            : [...state.clientProfileList.notesList, ...uniqueNotes]
        };
      })
      .addCase(getClientNotes.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getClientNote.pending, (state, { meta }) => {
        state.clientNotes[meta.arg.noteId] = {
          loading: true,
          data: null
        };
      })
      .addCase(getClientNote.fulfilled, (state, { payload, meta }) => {
        state.clientNotes[meta.arg.noteId] = {
          data: payload,
          loading: false
        };
      })
      .addCase(getClientNote.rejected, (_, { payload, error }: any) => {
        notifyUserError(payload);
      })
      .addCase(getAddendums.fulfilled, (state, { payload }) => {
        state.addendums.data = payload.content;
      })
      .addCase(getAddendums.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(postCreateAddendum.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(putEditAddendum.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(patchEditAddendum.fulfilled, (state, { payload, meta }) => {
        const addendumIdx = state.addendums.data.findIndex(
          (addendum) => addendum.id === meta.arg.addendumId
        );
        if (addendumIdx !== -1) {
          state.addendums.data[addendumIdx] = {
            ...state.addendums.data[addendumIdx],
            status: 'FINAL',
            content: meta.arg.content
          };
        }

        notifyUserSuccess('Your addendum was saved');
      })
      .addCase(patchEditAddendum.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(deleteAddendum.fulfilled, (state, { meta }) => {
        const filteredAddendums = state.addendums.data.filter(
          (addendum) => addendum.id !== meta.arg.addendumId
        );

        state.addendums.data = filteredAddendums;
      })
      .addCase(deleteAddendum.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(deleteNoteInput.fulfilled, (state, { meta }) => {
        state.clientProfileList.notesList = state.clientProfileList.notesList.filter(
          (note) => note.id.toString() !== meta.arg.noteId.toString()
        );
      })
      .addCase(getUsersImageNotes.fulfilled, (state: any, { payload }: any) => {
        if (payload.notePopup) {
          state.clientNotes[payload.noteId] = {
            data: {
              ...state.clientNotes[payload.noteId].data,
              author: {
                ...state.clientNotes[payload.noteId].data.author,
                profileImageUrl: URL.createObjectURL(new Blob([payload.data]))
              }
            },
            loading: false
          };
        }
        if (payload.noteAddendums) {
          const newAddendums = { ...state.addendums };
          const newAddendumsData =
            newAddendums.data.length &&
            newAddendums.data.map((a: Addendum) => {
              if (a.id.toString() === payload.noteId) {
                return {
                  ...a,
                  author: {
                    ...a.author,
                    profileImageUrl: URL.createObjectURL(new Blob([payload.data]))
                  }
                };
              }
              return a;
            });
          state.addendums = { ...state.addendums, data: newAddendumsData || state.addendums.data };
        }
        if (payload.notesList) {
          const foundNote = state.clientProfileList.notesList.findIndex(
            (a: ClientProfileContentItem) => a.id.toString() === payload.noteId
          );
          if (foundNote !== -1) {
            state.clientProfileList.notesList[foundNote] = {
              ...state.clientProfileList.notesList[foundNote],
              author: {
                ...state.clientProfileList.notesList[foundNote].author,
                profileImageUrl: URL.createObjectURL(new Blob([payload.data]))
              }
            };
          }
        }
      })
      .addCase(getUsersImageNotes.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getSites.pending, (state) => {
        state.sites.loading = true;
      })
      .addCase(getSites.fulfilled, (state, { payload }) => {
        state.sites.data = payload;
      })
      .addCase(getFavoriteZCodes.pending, (state) => {
        state.favoriteZCodes.loading = true;
      })
      .addCase(getFavoriteZCodes.fulfilled, (state, { payload }) => {
        state.favoriteZCodes.data = payload;
      })
      .addCase(getFavoriteZCodes.rejected, (_, { payload }: any) => {
        notifyUserError(payload);
      })
      .addCase(getZCodes.pending, (state) => {
        state.zCodes.loading = true;
      })
      .addCase(getZCodes.fulfilled, (state, { payload }) => {
        state.zCodes.data = payload;
      });
  }
});
export const {
  resetClientNotes,
  removeNote,
  deleteNoteInClientNotes,
  updateNoteInClientNotes,
  clearZCodes
} = clientProfileSlice.actions;
export default clientProfileSlice.reducer;
